import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import api from '../../api/endpoints';
import { fileUrl } from '../../api/client';
import {
  Avatar, Badge, Button, Empty, Loading, Rating,
} from '../../components/Ui';
import { Reveal } from '../../components/Motion';
import Gallery from '../../components/Gallery';
import PageHeader from '../../components/PageHeader';
import { colors } from '../../theme';
import { currency, discountPercent } from '../../utils/format';

const ServiceDetailPage = () => {
  // Opened from the partner panel with ?preview=1: same page, ordering switched off.
  const preview = new URLSearchParams(useLocation().search).get('preview') === '1';
  const { id } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.services.detail(id)
      .then((res) => setService(res.data))
      .catch(() => setService(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <Loading text="Loading service…" />;
  if (!service) {
    return <Empty emoji="🛠️" title="Service not found" actionLabel="Go back" onAction={() => navigate(-1)} />;
  }

  const off = discountPercent(service.mrp, service.price);
  const images = service.images || [];

  return (
    <div className="has-action-bar">
      <PageHeader title={service.title} subtitle={(service.partner && service.partner.businessName) || ''} />

      <div className="split wide-side">
        {/* Gallery + details */}
        <div>
          <Gallery
            images={images}
            fallback={(service.category && service.category.icon) || '🛠️'}
            emptyLabel="No photo for this service yet"
            badge={off > 0 ? (
              <span
                style={{
                  position: 'absolute', right: 12, bottom: 12, background: colors.accent,
                  color: '#fff', padding: '5px 11px', borderRadius: 8, fontWeight: 800, fontSize: 11.5,
                }}
              >
                {off}% OFF
              </span>
            ) : null}
          />
          {service.video ? (
            <div style={{ marginTop: 14 }}>
              <div className="t-small" style={{ fontWeight: 700, marginBottom: 6 }}>🎬 Video</div>
              <video src={fileUrl(service.video)} controls style={{ width: '100%', maxHeight: 420, borderRadius: 14, background: '#000' }} />
            </div>
          ) : null}

          <Reveal style={{ marginTop: 16 }}>
            <div className="row wrap" style={{ gap: 8 }}>
              <Rating value={service.rating} count={service.ratingCount} size={13} />
              <span className="t-small">·</span>
              <span className="t-small">⏱ {service.durationMinutes} mins</span>
              <span className="t-small">·</span>
              <span className="t-small">{service.bookingCount || 0} booked</span>
            </div>

            {service.description ? (
              <>
                <h3 className="h3" style={{ marginTop: 22 }}>About this service</h3>
                <p className="t-body" style={{ marginTop: 8, lineHeight: 1.7 }}>{service.description}</p>
              </>
            ) : null}

            {/* What the category asks about, then the provider's own details. */}
            {(service.attributes && service.attributes.length) || (service.specs && service.specs.length) ? (
              <>
                <h3 className="h3" style={{ marginTop: 22 }}>Details</h3>
                <div className="card" style={{ marginTop: 8, padding: 0, overflow: 'hidden' }}>
                  {[
                    ...((service.attributes || []).map((a) => [a.label, a.values.map((v) => (v === 'yes' ? 'Yes' : v === 'no' ? 'No' : v)).join(', ')])),
                    ...((service.specs || []).map((s) => [s.key, s.value])),
                  ].filter(([, v]) => v).map(([k, v], i) => (
                    <div key={`${k}-${i}`} className="row-between" style={{ padding: '10px 14px', borderTop: i ? '1px solid var(--line)' : 'none', gap: 12 }}>
                      <span className="t-small" style={{ color: 'var(--ink-2)' }}>{k}</span>
                      <span className="t-small" style={{ fontWeight: 600, textAlign: 'right' }}>{v}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : null}

            {service.inclusions && service.inclusions.length ? (
              <>
                <h3 className="h3" style={{ marginTop: 24, marginBottom: 8 }}>What is included</h3>
                {service.inclusions.map((i) => (
                  <div key={i} className="row" style={{ gap: 9, alignItems: 'flex-start', marginBottom: 7 }}>
                    <span style={{ color: colors.success }}>✓</span>
                    <span className="t-body grow">{i}</span>
                  </div>
                ))}
              </>
            ) : null}

            {service.exclusions && service.exclusions.length ? (
              <>
                <h3 className="h3" style={{ marginTop: 24, marginBottom: 8 }}>Not included</h3>
                {service.exclusions.map((i) => (
                  <div key={i} className="row" style={{ gap: 9, alignItems: 'flex-start', marginBottom: 7 }}>
                    <span style={{ color: colors.danger }}>✕</span>
                    <span className="t-body grow">{i}</span>
                  </div>
                ))}
              </>
            ) : null}
          </Reveal>
        </div>

        <div className="split-side">
          {preview ? (
          <div className="action-bar" style={{ background: 'var(--warning-bg)' }}>
            <div className="container row-between" style={{ gap: 12 }}>
              <div>
                <strong style={{ color: 'var(--warning)' }}>👀 Customer preview</strong>
                <div className="t-tiny">This is how customers see it. Ordering is off in preview.</div>
              </div>
              <Button title="Back to partner panel" variant="light" onClick={() => navigate(-1)} />
            </div>
          </div>
          ) : (
          <div className="action-bar">
            <div className="action-main">
              <div className="row" style={{ gap: 8, alignItems: 'baseline' }}>
                <span style={{ fontSize: 22, fontWeight: 800 }}>{currency(service.price)}</span>
                {off > 0 ? (
                  <span style={{ fontSize: 13, color: colors.faint, textDecoration: 'line-through' }}>
                    {currency(service.mrp)}
                  </span>
                ) : null}
              </div>
              <div className="t-tiny">{service.priceUnit}</div>
            </div>

            <div className="action-cta">
              <Button
                title="Book this service"
                icon="📅"
                full
                onClick={() => navigate(`/book/${service._id}`, { state: { service } })}
              />
            </div>

            <div className="action-extra t-tiny" style={{ textAlign: 'center', marginTop: 10, lineHeight: 1.6 }}>
              Free visit charge · pay after the service or online
            </div>
          </div>
          )}

          {service.partner ? (
            <div className="action-extra">
              <h3 className="h3" style={{ marginTop: 22, marginBottom: 10 }}>Service provider</h3>
              <button
                type="button"
                onClick={() => navigate(`/business/${service.partner._id}`)}
                className="card sh-sm press hoverable"
                style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left' }}
              >
                <Avatar name={service.partner.businessName} uri={fileUrl(service.partner.logo)} size={48} />
                <span className="grow">
                  <span className="t-title" style={{ display: 'block' }}>{service.partner.businessName}</span>
                  <span className="row" style={{ gap: 8, marginTop: 3 }}>
                    <Rating value={service.partner.rating} count={service.partner.ratingCount} size={12} />
                    <Badge
                      value={service.partner.isOpen ? 'Open' : 'Closed'}
                      color={service.partner.isOpen ? colors.success : colors.danger}
                    />
                  </span>
                </span>
                <span style={{ fontSize: 20, color: colors.faint }}>›</span>
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ServiceDetailPage;
